import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { pdfdocumentFetch } from "../../services/api/Pdf_Policies";
import PageHeader from "../Header";

/* ================= PDF ICON ================= */
const PdfIcon: React.FC = () => (
  <img
    src="https://olympic.ind.in/wp-content/themes/olympic-india/assets/images/pdf-svgrepo-com.svg"
    alt="PDF"
    className="w-5 h-5 mr-3 flex-shrink-0"
  />
);

/* ================= TYPES ================= */
interface PdfFile {
  id?: number;
  original_name: string;
  download_link: string;
}

interface DocumentItem {
  id: number;
  description: string | null;
  documents: PdfFile[];
  file_type: string;
  is_active: boolean;
  created_at: string;
}

interface PolicyCategory {
  id: number;
  name: string;
  full_name: string;
  slug: string;
  status: boolean;
  documents: DocumentItem[];
}

/* ================= COMPONENT ================= */
interface PdfPoliciesPageProps {
  slug: string;
}

const PdfPoliciesPage: React.FC<PdfPoliciesPageProps> = ({ slug }) => {
  const [category, setCategory] = useState<PolicyCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCategory = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await pdfdocumentFetch(`pdf-policies/${slug}`);

        const data = response?.data?.data;

        if (!data) {
          setError("No data found.");
          setCategory(null);
          return;
        }

        setCategory(data);
      } catch (err) {
        console.error("Error fetching documents:", err);
        setError("Failed to load documents.");
        setCategory(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [slug]);

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Loading..." slug={slug} />

        <div className="flex justify-center items-center py-20">
          <div className="text-gray-500 text-lg">Loading documents...</div>
        </div>
      </div>
    );
  }

  /* ================= ERROR ================= */
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <PageHeader title="Documents" slug={slug} />

        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h2 className="text-lg font-semibold text-red-600">
              Something went wrong
            </h2>

            <p className="text-sm text-red-500 mt-2">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  /* ================= NO CATEGORY ================= */
  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <PageHeader title="Documents" slug={slug} />

        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-800">Coming Soon</h2>

            <p className="text-gray-500 mt-2">
              Documents will be available here soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ================= CHECK DOCUMENTS ================= */
  const hasDocuments =
    Array.isArray(category.documents) &&
    category.documents.length > 0 &&
    category.documents.some(
      (doc) => Array.isArray(doc.documents) && doc.documents.length > 0,
    );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <PageHeader
        title={category.full_name || category.name}
        slug={category.slug}
      />

      <div className="max-w-4xl mx-auto p-6 space-y-12">
        {/* ================= COMING SOON ================= */}
        {!hasDocuments ? (
          <div className="bg-white p-10 text-center shadow-sm">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                <span className="text-2xl">📄</span>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-800">
              Coming Soon
            </h2>
          </div>
        ) : (
          /* ================= DOCUMENT CARDS ================= */
          <div className="grid gap-3">
            {category.documents.map((doc) =>
              Array.isArray(doc.documents) && doc.documents.length > 0
                ? doc.documents.map((file, idx) => (
                    <Link
                      key={file.id ?? `${doc.id}-${idx}`}
                      to={file.download_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-3 bg-white rounded-lg shadow hover:bg-gray-100 transition"
                    >
                      <PdfIcon />

                      <span className="text-sm md:text-base">
                        {file.original_name}
                      </span>
                    </Link>
                  ))
                : null,
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfPoliciesPage;
